import { useCallback, useEffect, useMemo } from 'react';
import Fuse from 'fuse.js';
import {
  getUnixTime,
  startOfDay,
  endOfDay,
  subYears,
  isSameYear,
  addDays,
  fromUnixTime,
  startOfMonth,
  endOfMonth,
  addMonths,
  isSameDay,
  min,
} from 'date-fns';
import {
  DateRangePicker,
  Panel,
  CardButton,
  ListBox,
  Pill,
  Button,
  BrowserIcon,
  OSIcon,
  SkeletonLine,
} from '@crazyegginc/hatch';
import { Doughnut } from 'react-chartjs-2';

import { ReactComponent as ArrowExpandIcon } from '@crazyegginc/hatch/dist/images/icon-arrow-expand.svg';
import { ReactComponent as NoIconIcon } from '@crazyegginc/hatch/dist/images/icon-browser.svg';

import { DashboardPage } from '/src/components/Page';
import { SEO } from '/src/components/SEO';
import { DashHeader } from '/src/components/headers/DashHeader';
import { SitePickerBar } from '/src/components/site-picker/index';

import { useSite, useQueryParams, usePermissions } from '/src/hooks';
import { getFeatureName } from '/src/features/_global/utils';
import { formatFullDate, formatShortFullDate, msToHumanDuration, TIMEZONE_OFFSET_SECONDS } from '/src/utils/date';
import { BROWSER_TYPES, FEATURES, OS_TYPES } from '/src/features/_global/constants';

import { SPECIAL_DATE_RANGES } from '/src/features/_global/constants';
import { NotInstalledWall } from '../walls/NotInstalled';
import { Chart } from '../components/chart';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { WEB_ANALYTIC_METRICS } from '../constants';
import { ReactComponent as ArrowIcon } from '@crazyegginc/hatch/dist/images/icon-arrow.svg';
import { abbreviateNumber } from '/src/utils';
import ReactCountryFlag from 'react-country-flag';
import { AnalyticsTable, columnOrderAtom, getDefaultOrder, searchAtom } from '../components/table';
import { webAnalyticsQuery } from '../queries';
import { useQuery } from '@tanstack/react-query';
import { percentizeValue } from '../utils';
import { capitalizeFirstLetter, toTitleCase } from '/src/utils/string';
import { DashboardPaywall } from '../../_global/paywalls/DashboardPaywall';
import { SkeletonChart } from '../components/skeleton-chart';
import classNames from 'classnames';
import { NoMatchesWall } from '../../_global/paywalls/NoMatchesWall';
import { useAtom } from 'jotai';
import { ensureValidSearch } from '/src/utils/url';
import { scrollToLater } from '/src/utils/scroll';

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });

const getVisitorsDonutData = (input) => {
  return {
    labels: ['New', 'Returning'],
    datasets: [
      {
        data: [input?.new ?? 0, input?.returning ?? 0],
        backgroundColor: ['#5B61F4', '#5AC8FA'],
        borderColor: '#fff',
        borderWidth: 4,
        cutout: '70%',
        hoverOffset: 8,
        hoverBorderColor: '#fff',
      },
    ],
  };
};

const getDeviceDonutData = (input) => {
  const labels = input?.map?.((d) => d.name) ?? [];
  const data = input?.map?.((d) => d.visitors) ?? [];

  return {
    labels,
    datasets: [
      {
        data,
        backgroundColor: ['#DF6061', '#FCC34A', '#2672FA'],
        borderColor: '#fff',
        borderWidth: 4,
        cutout: '70%',
        hoverOffset: 8,
        hoverBorderColor: '#fff',
      },
    ],
  };
};

// eslint-disable-next-line no-restricted-syntax
export default function WebAnalyticsDashboard() {
  useSite(); // select the default site
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.WEB_ANALYTICS);

  useEffect(() => {
    // set default params
    const newParams = new URLSearchParams(window.location.search);

    if (!newParams.has('date_range')) newParams.set('date_range', SPECIAL_DATE_RANGES.LAST_30_DAYS);
    if (!newParams.has('metric')) newParams.set('metric', WEB_ANALYTIC_METRICS.UNIQUE_VISITORS);

    setSearchParams(newParams, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    return () => {
      // unset any stored scroll position to avoid scrolling if we return from
      // another page.
      window.WEB_ANALYTICS_SCROLLPOS = undefined;
    };
  }, []);

  const params = Object.fromEntries(searchParams.entries());

  if (!params.site || !params.date_range || !params.metric) {
    return null;
  }

  if (!canViewDashboard) {
    return <DashboardPaywall reason={reason} feature={FEATURES.HOME} />;
  }

  return (
    <DashboardPage>
      <SEO title="Web Analytics" />
      <DashHeader titleComponent={getFeatureName(FEATURES.WEB_ANALYTICS)} />

      <WebAnalyticsContent />
    </DashboardPage>
  );
}

function parseDateRange(string) {
  if (!string) return [null, null, null];
  switch (string) {
    case SPECIAL_DATE_RANGES.TODAY:
    case SPECIAL_DATE_RANGES.LAST_7_DAYS:
    case SPECIAL_DATE_RANGES.LAST_30_DAYS:
    case SPECIAL_DATE_RANGES.THIS_MONTH:
    case SPECIAL_DATE_RANGES.LAST_MONTH:
      return [null, null, string];
    default:
      if (string.includes(',')) {
        return [...string.split(',').map((part) => parseInt(part)), null];
      } else {
        return [null, null, null];
      }
  }
}

function humanizeDateRange([startDate, endDate, predefinedRange]) {
  if (predefinedRange) {
    const [predefinedStart, predefinedEnd] = parsePredefinedRange(predefinedRange);

    const parsedStart = fromUnixTime(predefinedStart);
    const parsedEnd = fromUnixTime(predefinedEnd);

    if (isSameDay(parsedEnd, parsedStart)) {
      return `${formatFullDate(predefinedEnd)}`;
    }
    if (isSameYear(parsedEnd, parsedStart)) {
      return `${formatShortFullDate(predefinedStart)} - ${formatFullDate(predefinedEnd)}`;
    } else {
      return `${formatFullDate(predefinedStart)} - ${formatFullDate(predefinedEnd)}`;
    }
  } else {
    const parsedStart = fromUnixTime(startDate);
    const parsedEnd = fromUnixTime(endDate);

    if (isSameDay(parsedEnd, parsedStart)) {
      return `${formatFullDate(endDate)}`;
    }
    if (isSameYear(parsedEnd, parsedStart)) {
      return `${formatShortFullDate(startDate)} - ${formatFullDate(endDate)}`;
    } else {
      return `${formatFullDate(startDate)} - ${formatFullDate(endDate)}`;
    }
  }
}

function parsePredefinedRange(string) {
  const now = Date.now();
  let startDate, endDate;
  switch (string) {
    case SPECIAL_DATE_RANGES.TODAY:
      startDate = startOfDay(now) / 1000;
      endDate = endOfDay(now) / 1000;
      break;
    case SPECIAL_DATE_RANGES.LAST_7_DAYS:
      startDate = startOfDay(addDays(now, -7)) / 1000;
      endDate = endOfDay(now) / 1000;
      break;
    case SPECIAL_DATE_RANGES.LAST_30_DAYS:
      startDate = startOfDay(addDays(now, -30)) / 1000;
      endDate = endOfDay(now) / 1000;
      break;
    case SPECIAL_DATE_RANGES.THIS_MONTH:
      startDate = startOfMonth(now) / 1000;
      endDate = min([endOfMonth(now), endOfDay(now)]) / 1000;
      break;
    case SPECIAL_DATE_RANGES.LAST_MONTH:
      startDate = startOfMonth(addMonths(now, -1)) / 1000;
      endDate = endOfMonth(addMonths(now, -1)) / 1000;
      break;
  }

  return [startDate, endDate];
}

function WebAnalyticsContent() {
  const { sites, loadingSites, selectedSite, selectSite } = useSite();

  // if (!selectedSite) return null;

  return (
    <>
      <SitePickerBar
        sites={sites}
        loading={loadingSites}
        selectedSite={selectedSite}
        selectSite={selectSite}
        enableAllSites={true}
      />
      <div className="flex flex-col p-10">
        {selectedSite && !selectedSite.installed ? <NotInstalledWall /> : <RenderAnalytics />}
      </div>
    </>
  );
}

function formatOsName(os) {
  switch (os.toUpperCase().replaceAll(' ', '_')) {
    case OS_TYPES.WINDOWS:
    case 'WIN32':
      return 'Windows';
    case OS_TYPES.MACOS:
    case 'DARWIN':
      return 'MacOS';
    case OS_TYPES.ANDROID:
      return 'Android';
    case OS_TYPES.IOS:
      return 'iOS';
    case OS_TYPES.LINUX:
      return 'Linux';
    case OS_TYPES.WINDOWS_PHONE:
      return 'Windows Phone';
    case OS_TYPES.UBUNTU:
      return 'Ubuntu';
    case OS_TYPES.CHROMEOS:
      return 'ChromeOS';
    case OS_TYPES.CHROMIUM_OS:
      return 'Chromium OS';
    case OS_TYPES.OTHER:
      return 'Unknown';
    default:
      return os;
  }
}

function formatBrowserName(browser) {
  switch (browser.toUpperCase().replaceAll(' ', '_')) {
    case BROWSER_TYPES.GSA:
      return 'Google Search Appliance';
    default:
      return browser;
  }
}

function getYScaleCallback(metric) {
  if (metric === 'bounceRate') {
    return (value) => String(value * 100) + '%';
  }
  if (metric === 'avgSessionDuration') {
    return (value) => {
      let val;
      if (typeof value === 'string') {
        val = Number(value.replaceAll(',', ''));
      } else {
        val = value;
      }
      return msToHumanDuration(val);
    };
  }
  return (value) => value;
}

function getMetricName(metric) {
  switch (metric) {
    case 'uniqueVisitors':
      return 'Visitors';
    case 'pageViews':
      return 'Views';
    case 'avgSessionDuration':
      return 'Duration';
    case 'sessions':
      return 'Sessions';
    case 'bounceRate':
      return 'Rate';
  }
}

// Only run queries in this component, we use the ones above
// to ensure the params are set and the site is loaded.
function RenderAnalytics() {
  const { selectedSite } = useSite();
  const { get: paramGet, set: paramSet } = useQueryParams();
  const [, setOrder] = useAtom(columnOrderAtom);
  const [, setSearch] = useAtom(searchAtom);
  const { view } = useParams();
  const navigate = useNavigate();

  const dateRange = paramGet('date_range');

  const parsedDateRange = parseDateRange(dateRange);

  const [startDate, endDate, predefinedRange] = parsedDateRange;

  const { data, isLoading } = useQuery({
    ...webAnalyticsQuery({
      view: view || 'ALL',
      siteId: selectedSite?.id,
      startAt: startDate - TIMEZONE_OFFSET_SECONDS,
      endAt: endDate - TIMEZONE_OFFSET_SECONDS,
      presetDate: predefinedRange,
      limit: view ? -1 : 6,
    }),
    staleTime: 0,
    refetchOnMount: 'always',
    enabled: Boolean(selectedSite),
    onSettled() {
      if (view === undefined && window.WEB_ANALYTICS_SCROLLPOS && window.scrollY === 0) {
        scrollToLater(window.WEB_ANALYTICS_SCROLLPOS, () => {
          window.WEB_ANALYTICS_SCROLLPOS = undefined;
        });
      } else if (view === undefined && window.WEB_ANALYTICS_SCROLLPOS) {
        window.WEB_ANALYTICS_SCROLLPOS = undefined;
      }
    },
  });

  useEffect(() => {
    if (startDate === null && endDate === null && predefinedRange === null) {
      paramSet('date_range', SPECIAL_DATE_RANGES.LAST_30_DAYS);
    }
  }, [paramSet, startDate, endDate, predefinedRange]);

  // const selectedMetric = params.metric;

  const webAnalyticsData = useMemo(() => {
    if (!data?.analytics) return null;

    const result = {
      ...data?.analytics,
      countries: data.analytics?.countries?.map?.((c) => {
        return {
          ...c,
          name: countryNames.of(c.name),
          code: c.name,
        };
      }),
      languages: data.analytics?.languages?.map?.((l) => {
        return {
          ...l,
          name: languageNames.of(l.name.split('-')[0]),
          code: l.name,
        };
      }),
      os: data.analytics?.os?.map?.((o) => {
        return {
          ...o,
          name: formatOsName(o.name),
        };
      }),
      deviceType: data.analytics?.deviceType?.map?.((dt) => {
        return {
          ...dt,
          name: capitalizeFirstLetter(dt.name),
        };
      }),
    };

    const filtered = Object.fromEntries(
      Object.entries(result).filter(([_, value]) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        if (typeof value === 'object' && value !== null && 'total' in value) {
          return value.total !== 0;
        }
        if (typeof value === 'object' && value !== null && 'days' in value && Array.isArray(value.days)) {
          return value.days.length > 0;
        }
      }),
    );

    return filtered;
  }, [data]);

  const chooseDateRange = useCallback(
    (value) => {
      if (value.special) {
        paramSet('date_range', value.special);
      } else if (value.start_date && value.end_date) {
        const newStartDate = getUnixTime(value.start_date);
        const newEndDate = getUnixTime(value.end_date);

        paramSet('date_range', `${newStartDate},${newEndDate}`);
      }
    },
    [paramSet],
  );

  const humanizedDateRange = humanizeDateRange(parsedDateRange);

  // const hasData = true // Object.keys(webAnalyticsData).length > 0;
  const hasData = Object.keys(webAnalyticsData || {}).length > 0;

  return (
    <>
      <div className="mx-auto flex w-full min-w-[940px] max-w-[1024px] flex-col ">
        {view ? (
          <div>
            <Button
              variant="cancel"
              onClick={() => {
                // paramRemoveAll(['search', 'order']);
                setSearch('');
                setOrder(getDefaultOrder());
                navigate({
                  pathname: '/web-analytics',
                  search: ensureValidSearch(window.location.search, ['site', 'date_range', 'metric']),
                });
              }}
              className="!px-1 flex items-center space-x-1"
            >
              <ArrowIcon className="rotate-180 w-2.5 h-2.5 fill-current" />
              <span>Back</span>
            </Button>
          </div>
        ) : null}

        <div className="mb-2.5 flex justify-between items-center">
          <div className="ml-1 font-semibold text-lg text-black-pearl-500">{humanizedDateRange}</div>
          <div className="mr-1">
            <DateRangePicker
              lowerBoundary={startOfDay(subYears(new Date(), 2))}
              upperBoundary={endOfDay(new Date())}
              startDate={startDate ? startDate * 1000 : undefined}
              endDate={endDate ? endDate * 1000 : undefined}
              special={predefinedRange}
              setDateRange={chooseDateRange}
              setCommonDateRange={chooseDateRange}
              showCommonRanges={true}
              size="lg"
              maxRange={365}
            />
          </div>
        </div>

        {hasData || view || isLoading ? (
          view ? (
            <DetailTableView isLoading={isLoading} analytics={webAnalyticsData} selectedSite={selectedSite} />
          ) : (
            <MainDashboard isLoading={isLoading} analytics={webAnalyticsData} />
          )
        ) : (
          <NoMatchesWall
            text={
              <span>
                data with the selected date range.
                <br />
                Try changing the date range
              </span>
            }
          />
        )}

        {!view && hasData ? (
          <Button
            variant="cancel"
            className="justify-end w-full space-x-1.5 items-center mt-3.75 flex"
            onClick={() => scrollToLater(0)}
          >
            <ArrowIcon className="-rotate-90 fill-current w-2.5 h-2.5" />
            <span>Back to top</span>
          </Button>
        ) : null}
      </div>
    </>
  );
}

function Header({ slug, title, subTitle, label }) {
  const navigate = useNavigate();

  return (
    <div className="mb-2.5 flex w-full items-end justify-between">
      <button
        onClick={() => {
          window.WEB_ANALYTICS_SCROLLPOS = window.scrollY;
          navigate({
            pathname: `/web-analytics/${slug}`,
            search: window.location.search,
          });
        }}
        className="flex items-center text-base font-semibold text-black-pearl-500 cursor-pointer"
      >
        {title}
        <ArrowExpandIcon className="ml-2.5 h-2.5 w-2.5 rotate-90 fill-current opacity-50" aria-label={label} />
      </button>
      <span className="text-sm font-semibold text-black-pearl-500">{subTitle}</span>
    </div>
  );
}

function DeltaPill({ delta, positiveColor = 'green', negativeColor = 'red' }) {
  const value = percentizeValue(delta);
  const sign = Math.sign(delta);
  const color = sign === 1 ? positiveColor : negativeColor;

  if (sign === 0) return null;

  return (
    <Pill arrowIndicator={sign} colour={color}>
      {value}
    </Pill>
  );
}

function MainDashboard({ analytics, isLoading = false }) {
  const { get: paramGet, set: paramSet, remove: paramRemove } = useQueryParams();

  const selectedMetric = paramGet('metric');

  const selectMetric = useCallback(
    (newValue) => {
      if (newValue) {
        paramSet('metric', newValue);
      }
    },
    [paramSet],
  );

  const selectedMetricData = useMemo(() => {
    return analytics?.[selectedMetric] ?? {};
  }, [analytics, selectedMetric]);

  useEffect(() => {
    if (!selectedMetricData) {
      paramRemove('metric');
    }
  }, [selectedMetricData, paramRemove]);

  const hasSelectedMetricData = Object.values(selectedMetricData).length > 0;

  return (
    <div className="flex flex-col w-full space-y-5">
      <Panel variant="secondary">
        <div className={classNames('flex justify-around w-full', { 'mb-4': isLoading || hasSelectedMetricData })}>
          {analytics?.uniqueVisitors || isLoading ? (
            <CardButton
              title="Unique visitors"
              active={selectedMetric === WEB_ANALYTIC_METRICS.UNIQUE_VISITORS}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.UNIQUE_VISITORS)}
              isLoading={isLoading}
            >
              <div className="flex items-center">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500">
                  {abbreviateNumber(analytics?.uniqueVisitors?.total)}
                </span>
                <DeltaPill delta={analytics?.uniqueVisitors?.delta} />
              </div>
            </CardButton>
          ) : null}
          {analytics?.pageViews || isLoading ? (
            <CardButton
              title="Pageviews"
              active={selectedMetric === WEB_ANALYTIC_METRICS.PAGE_VIEWS}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.PAGE_VIEWS)}
              isLoading={isLoading}
            >
              <div className="flex items-center">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500">
                  {abbreviateNumber(analytics?.pageViews?.total)}
                </span>
                <DeltaPill delta={analytics?.pageViews?.delta} />
              </div>
            </CardButton>
          ) : null}
          {analytics?.avgSessionDuration || isLoading ? (
            <CardButton
              title="Avg. session duration"
              active={selectedMetric === WEB_ANALYTIC_METRICS.AVG_SESSION_DURATION}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.AVG_SESSION_DURATION)}
              isLoading={isLoading}
            >
              <div className="flex items-center">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500">
                  {msToHumanDuration(analytics?.avgSessionDuration?.total)}
                </span>
                <DeltaPill delta={analytics?.avgSessionDuration?.delta} />
              </div>
            </CardButton>
          ) : null}
          {analytics?.sessions || isLoading ? (
            <CardButton
              title="Sessions"
              active={selectedMetric === WEB_ANALYTIC_METRICS.SESSIONS}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.SESSIONS)}
              isLoading={isLoading}
            >
              <div className="flex items-center">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500">
                  {abbreviateNumber(analytics?.sessions?.total)}
                </span>
                <DeltaPill delta={analytics?.sessions?.delta} />
              </div>
            </CardButton>
          ) : null}
          {analytics?.bounceRate || isLoading ? (
            <CardButton
              title="Bounce rate"
              active={selectedMetric === WEB_ANALYTIC_METRICS.BOUNCE_RATE}
              onClick={() => selectMetric(WEB_ANALYTIC_METRICS.BOUNCE_RATE)}
              isLoading={isLoading}
            >
              <div className="flex items-center">
                <span className="mr-2.5 block truncate text-[30px] font-bold leading-none text-black-pearl-500">
                  {percentizeValue(analytics?.bounceRate?.total)}
                </span>
                <DeltaPill delta={analytics?.bounceRate?.delta} positiveColor="red" negativeColor="green" />
              </div>
            </CardButton>
          ) : null}
        </div>
        <div className={classNames('w-full', { 'h-[250px]': isLoading || hasSelectedMetricData })}>
          {isLoading ? (
            <SkeletonChart />
          ) : (
            <>
              {hasSelectedMetricData ? (
                <Chart
                  data={selectedMetricData}
                  yScaleCallback={getYScaleCallback(selectedMetric)}
                  getMetricName={getMetricName(selectedMetric)}
                />
              ) : null}
            </>
          )}
        </div>
      </Panel>
      <ul className="grid w-full grid-cols-3 grid-rows-auto gap-5">
        {analytics?.visitors || isLoading ? (
          <DonutPanel
            title="New vs returning visitors"
            subtitle="Visitors"
            data={getVisitorsDonutData(analytics?.visitors)}
            isLoading={isLoading}
            size="180px"
            skeletonClassName="!w-[180px] !h-[180px]"
          />
        ) : null}
        {analytics?.countries || isLoading ? (
          <CountriesPanel data={analytics?.countries} isLoading={isLoading} />
        ) : null}
        {analytics?.languages || isLoading ? (
          <RelativeBarChartPanel slug="languages" title="Languages" data={analytics?.languages} isLoading={isLoading} />
        ) : null}
        {analytics?.deviceType || isLoading ? (
          <DonutPanel
            title="Device type"
            subtitle="Devices"
            size="140px"
            skeletonClassName="!w-[140px] !h-[140px]"
            data={getDeviceDonutData(analytics?.deviceType)}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.os || isLoading ? (
          <IconBarChartPanel
            title="OS"
            slug="os"
            subTitle="Visitors"
            data={analytics?.os}
            getIcon={(title) => (
              <OSIcon
                className="w-4 h-4"
                os={title.toUpperCase().replaceAll(' ', '_')}
                variant="COLOR"
                renderUnknown={true}
              />
            )}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.browsers || isLoading ? (
          <IconBarChartPanel
            title="Browsers"
            slug="browsers"
            subTitle="Visitors"
            data={analytics?.browsers}
            getIcon={(title) => (
              <BrowserIcon
                className="w-4 h-4"
                browser={title.toUpperCase().replaceAll(' ', '_')}
                variant="COLOR"
                renderUnknown={true}
              />
            )}
            getText={(title) => {
              return formatBrowserName(title);
            }}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.channels || isLoading ? (
          <RelativeBarChartPanel slug="channels" title="Channels" data={analytics?.channels} isLoading={isLoading} />
        ) : null}
        {analytics?.referrers || isLoading ? (
          <RelativeBarChartPanel title="Referrers" slug="referrers" data={analytics?.referrers} isLoading={isLoading} />
        ) : null}
        {analytics?.pages || isLoading ? (
          <RelativeBarChartPanel
            slug="pages"
            title="Pages"
            valueLabel="Pageviews"
            valueKey="pageViews"
            data={analytics?.pages}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.entryPages || isLoading ? (
          <RelativeBarChartPanel
            slug="entryPages"
            title="Entry pages"
            data={analytics?.entryPages}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.utmCampaigns ? (
          <RelativeBarChartPanel
            slug="utmCampaigns"
            title="Campaigns"
            data={analytics.utmCampaigns}
            isLoading={isLoading}
          />
        ) : null}
        {analytics?.utmSources ? (
          <RelativeBarChartPanel slug="utmSources" title="Source" data={analytics.utmSources} isLoading={isLoading} />
        ) : null}
        {analytics?.utmMediums ? (
          <RelativeBarChartPanel slug="utmMediums" title="Medium" data={analytics.utmMediums} isLoading={isLoading} />
        ) : null}
        {analytics?.utmTerms ? (
          <RelativeBarChartPanel slug="utmTerms" title="Terms" data={analytics.utmTerms} isLoading={isLoading} />
        ) : null}
        {analytics?.utmContents ? (
          <RelativeBarChartPanel
            slug="utmContents"
            title="Content"
            data={analytics?.utmContents}
            isLoading={isLoading}
          />
        ) : null}
      </ul>
    </div>
  );
}

let fuse;

function DetailTableView({ analytics, selectedSite, isLoading }) {
  const [search, setSearch] = useAtom(searchAtom);
  const [columnOrder, setOrder] = useAtom(columnOrderAtom);
  const { view } = useParams();

  useEffect(() => {
    // ensure we reset the order to stay in sync with the url params
    setOrder(getDefaultOrder());
    setSearch('');
    return () => {
      setOrder(getDefaultOrder());
      setSearch('');
    };
  }, [setSearch, setOrder]);

  const filteredData = useMemo(() => {
    if (!analytics?.[view]) {
      return [];
    }
    fuse = new Fuse(analytics[view], {
      keys: ['name'],
      threshold: 0.3,
    });
    if (!search) {
      return analytics?.[view];
    }
    const results = fuse.search(search);
    return results.map((r) => r.item);
  }, [search, analytics, view]);

  const sortedData = useMemo(() => {
    if (filteredData.length === 0) return [];

    if (!columnOrder) return filteredData;

    const key = columnOrder.id;
    const desc = columnOrder.desc;
    const sampleValue = filteredData[0][key];

    return [...filteredData].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];

      if (typeof sampleValue === 'string') {
        const result = aVal.localeCompare(bVal, undefined, { numeric: true });
        return desc ? -result : result;
      }

      if (typeof sampleValue === 'number') {
        return desc ? bVal - aVal : aVal - bVal;
      }

      // fallback for unknown types
      const result = String(aVal).localeCompare(String(bVal));
      return desc ? -result : result;
    });
  }, [filteredData, columnOrder]);

  return (
    <AnalyticsTable
      selected={view}
      title={slugToTitle(view)}
      analytics={sortedData}
      selectedSite={selectedSite}
      isLoading={isLoading}
    />
  );
}

function slugToTitle(slug) {
  if (slug === 'os') return 'Operating Systems';

  if (slug.includes('utm')) {
    return toTitleCase(slug).replace('Utm', 'UTM');
  }

  return toTitleCase(slug);
}

function DonutPanel({ title, subtitle, data, isLoading = false, size = `120px`, skeletonClassName }) {
  const total =
    (data?.datasets ?? [])?.[0]?.data?.reduce?.((sum, value) => {
      return sum + value;
    }, 0) ?? 0;

  const largestValue =
    (data?.datasets ?? [])?.[0]?.data?.reduce?.((current, value) => {
      if (value > current) {
        return value;
      }
      return current;
    }, 0) ?? 0;

  if (total === 0) {
    return null;
  }

  return (
    <li>
      <Panel variant="secondary" secondaryHover={true} className="w-full h-full">
        <h2 className="font-semibold mb-2">{title}</h2>
        <div className="relative self-center" style={{ width: size, height: size }}>
          {isLoading ? (
            <SkeletonLine className={skeletonClassName} style={{ width: size, height: size }} />
          ) : (
            <>
              <Doughnut
                data={data}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      enabled: false,
                    },
                  },
                }}
              />
              <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 flex flex-col items-center">
                <span className="font-bold text-[26px] leading-none">{abbreviateNumber(total).toLowerCase()}</span>
                <span className="text-graphite-shadow-500 text-xs leading-none">{subtitle}</span>
              </div>
            </>
          )}
        </div>
        <div className="flex flex-col w-full text-charcoal-gray-500 text-sm space-y-4 mt-3 divide divide-[#979797]">
          {isLoading ? (
            <>
              <SkeletonLine className="w-full" />
              <SkeletonLine />
            </>
          ) : (
            (data?.datasets?.[0]?.data ?? []).map((dataValue, idx) => (
              <div
                className={classNames('flex items-center justify-between w-full', {
                  'font-bold': dataValue === largestValue,
                })}
                key={`${title.toLowerCase().replaceAll(' ', '_')}+${idx}`}
              >
                <div className="flex items-center space-x-2 truncate">
                  <span
                    className="w-2 h-2 rounded-full leading-none flex-shrink-0"
                    style={{ backgroundColor: data?.datasets?.[0].backgroundColor?.[idx] }}
                  />
                  <span className="leading-none truncate">{data.labels[idx]}</span>
                </div>
                <div className="flex items-center justify-between w-24 flex-shrink-0">
                  <span className="leading-none">{`${((dataValue / total) * 100).toFixed(1)}%`}</span>
                  <span className="leading-none">{abbreviateNumber(dataValue).toLowerCase()}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </Panel>
    </li>
  );
}

function CountriesPanel({ data, isLoading = false }) {
  const maxValue = data ? Math.max(...data.map((country) => country.visitors)) : 0;

  return (
    <li>
      <Panel variant="secondary" secondaryHover={true} className="h-full">
        <Header slug="countries" title="Countries" subTitle="Visitors" label="View countries more details" />
        <ul className="w-full space-y-1.25">
          {isLoading ? (
            <SkeletonPanelContent />
          ) : (
            data?.map?.((country, index) => {
              const { code, name, visitors } = country;
              return (
                <li className="flex w-full items-center text-sm" key={`${code}+${index}`}>
                  <ListBox withBackground={true} use={Math.floor((visitors / maxValue) * 100)}>
                    <ReactCountryFlag
                      countryCode={code}
                      svg
                      alt={name}
                      style={{
                        width: '18px',
                        height: '18px',
                        border: '1px solid #e1e6ef',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        overflow: 'hidden',
                        flexShrink: '0',
                      }}
                    />
                    <span className="truncate ml-1.5 text-black-pearl-500">{name}</span>
                  </ListBox>
                  <span className="ml-2 w-[30px] text-right text-black-pearl-500">{abbreviateNumber(visitors)}</span>
                </li>
              );
            })
          )}
        </ul>
      </Panel>
    </li>
  );
}

function SkeletonPanelContent() {
  const items = new Array(6).fill(true);
  return (
    <>
      {items.map(() => (
        <li className="flex w-full" key={Math.random()}>
          <SkeletonLine className="w-full !h-[36px] !rounded" />
        </li>
      ))}
    </>
  );
}

function RelativeBarChartPanel({
  title,
  slug,
  valueLabel = 'Visitors',
  valueKey = 'visitors',
  label,
  data,
  isLoading = false,
}) {
  const maxValue = data ? Math.max(...data.map((d) => d[valueKey])) : 0;
  const hasIcons = data?.some?.((d) => d?.icon) ?? false;

  const sortedData = data?.sort((a, b) => b[valueKey] - a[valueKey]);

  return (
    <li>
      <Panel variant="secondary" secondaryHover={true} className="h-full">
        <Header slug={slug} title={title} subTitle={valueLabel} label={label} />
        <ul className="w-full space-y-1.25">
          {isLoading ? (
            <SkeletonPanelContent />
          ) : (
            sortedData?.map?.((d, index) => {
              return (
                <li className="flex w-full items-center text-sm" key={`${slug}+${index}`}>
                  <ListBox withBackground={true} use={Math.floor((d[valueKey] / maxValue) * 100)}>
                    {hasIcons ? (
                      <div className="flex items-center truncate">
                        {renderIcon(d.icon)}
                        <span className="truncate ml-1.5 text-black-pearl-500">{d.name}</span>
                      </div>
                    ) : (
                      <span className="truncate text-black-pearl-500">{d.name}</span>
                    )}
                  </ListBox>
                  <span className="ml-2 w-[30px] text-right text-black-pearl-500">{abbreviateNumber(d[valueKey])}</span>
                </li>
              );
            })
          )}
        </ul>
      </Panel>
    </li>
  );
}

function renderIcon(icon) {
  if (!icon) {
    return <NoIconIcon className="w-4 h-4 text-lynch-500 fill-current" />;
  }
  if (typeof icon === 'string') {
    return <img className="h-4 w-4" src={icon} alt="" />;
  }

  return icon;
}

function IconBarChartPanel({
  title,
  slug,
  iconKey = 'name',
  nameKey = 'name',
  valueLabel,
  valueKey,
  data,
  isLoading,
  getIcon = () => {},
  getText = (name) => name,
}) {
  const modifiedData =
    data?.map?.((d) => {
      return {
        ...d,
        icon: getIcon(d[iconKey]),
        name: getText(d[nameKey]),
      };
    }) ?? data;

  return (
    <RelativeBarChartPanel
      title={title}
      slug={slug}
      valueLabel={valueLabel}
      valueKey={valueKey}
      data={modifiedData}
      isLoading={isLoading}
    />
  );
}
